import PropTypes from 'prop-types';
import React from 'react';
import { Common } from 'ws-scripts/modules/common';

import DisclaimersModal from './DisclaimersModal';
import {
	PLEASE_CALL,
	DETAILED_PRICING,
	CUSTOMIZE_PAYMENTS
} from '../constants/labels';
import { BUTTON_RESET_CLASSES } from '../constants/constants';
import { disclaimerDetailsModalEvents } from '../utils/analytics';
import { setClassNames, filterFinal, filterLoanLease } from '../utils/utility';
import PricingSummaryFinalPrice from './PricingSummaryFinalPrice';
import PricingSummaryPayments from './PricingSummaryPayments';
import PricingSummaryLink from './PricingSummaryLink';
import { CaDisclaimer } from './CanadaDisclaimerContent';
import './PricingSummary.scss';

const getFormattedValue = formatted => {
	let formattedValue = formatted;
	if (formattedValue === PLEASE_CALL) {
		formattedValue = (
			<Common
				render={({ labels }) => {
					return labels.get(PLEASE_CALL);
				}}
			/>
		);
	}
	return formattedValue;
};

export default class PricingSummaryUpdated extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			isModalVisible: false,
			paymentInfo: null
		};
		this.closeModal = this.closeModal.bind(this);
	}

	closeModal() {
		const { widgetName, windowId } = this.props.widgetInfo;
		disclaimerDetailsModalEvents(widgetName, windowId, false);
		this.setState({
			isModalVisible: false
		});
	}

	handleHelpCircleIcon(paymentInfo) {
		const { widgetName, windowId } = this.props.widgetInfo;
		disclaimerDetailsModalEvents(widgetName, windowId, true);
		this.setState({
			isModalVisible: true,
			paymentInfo
		});
	}

	render() {
		const { summary = [], isMobile, viewPriceDetail } = this.props;
		const { showStickySidebarLoanLease } = this.props.prefs;
		const finalLabelSize = isMobile
			? 'ddc-font-size-base'
			: 'ddc-font-size-small';
		const finalPriceSize = isMobile
			? 'ddc-font-size-xxlarge'
			: 'ddc-font-size-xlarge';
		const paymentLabelSize = isMobile
			? 'ddc-font-size-small'
			: 'ddc-font-size-xsmall';
		const paymentPriceSize = isMobile
			? 'ddc-font-size-xlarge'
			: 'ddc-font-size-large';
		const summaryLength = summary.length;
		let pricingSummary;

		if (summaryLength === 0) {
			return null;
		} else if (summaryLength > 1 && showStickySidebarLoanLease === 'true') {
			const finalPriceSummary = filterFinal(summary);
			const paymentsSummary = filterLoanLease(summary);

			pricingSummary = (
				<div>
					<DisclaimersModal
						isModalVisible={this.state.isModalVisible}
						handleClose={this.closeModal}
						paymentInfo={this.state.paymentInfo}
					/>
					<ul className="pricing-summary list-unstyled d-flex align-items-end justify-items-between mb-4">
						<li
							className={setClassNames(
								summaryLength <= 2
									? 'half-width'
									: 'third-width',
								' d-flex',
								'flex-wrap',
								'mr-4',
								'summary-price'
							)}
						>
							{finalPriceSummary.map(price => (
								<PricingSummaryFinalPrice
									{...{
										price,
										viewPriceDetail,
										finalLabelSize,
										finalPriceSize,
										paymentLabelSize
									}}
									key={price._type}
									buttonResetClasses={BUTTON_RESET_CLASSES}
									finalPrice={getFormattedValue(
										price.formatted
									)}
								/>
							))}
							<PricingSummaryLink
								label={DETAILED_PRICING}
								labelFontSize={paymentLabelSize}
								isCalculatePayment={false}
								viewPriceDetail={viewPriceDetail}
							/>
						</li>
						<li className="d-flex flex-wrap flex-grow-1">
							{paymentsSummary.map(price => (
								<PricingSummaryPayments
									{...{
										price,
										paymentLabelSize,
										paymentPriceSize
									}}
									key={price._type}
									paymentPrice={getFormattedValue(
										price.formatted
									)}
									handleHelpCircleIcon={() =>
										this.handleHelpCircleIcon(price)
									}
								/>
							))}
							<PricingSummaryLink
								label={CUSTOMIZE_PAYMENTS}
								labelFontSize={paymentLabelSize}
								isCalculatePayment
							/>
						</li>
					</ul>
				</div>
			);
		} else {
			// single item (final price) will get detailed pricing markup
			// summary[0] will always be final price - ref: VCDApricing.js
			const { label, formatted } = summary[0];
			const { dealerAddress } = this.props;

			pricingSummary = (
				<dl className="pricing-detail final-price line-height-condensed mb-4">
					<dt>
						<button
							className={BUTTON_RESET_CLASSES}
							onClick={viewPriceDetail}
						>
							<span dangerouslySetInnerHTML={{ __html: label }} />
							<PricingSummaryLink
								label={DETAILED_PRICING}
								labelFontSize={paymentLabelSize}
							/>
						</button>
					</dt>
					<dd
						className={setClassNames(
							'font-weight-bold',
							finalPriceSize
						)}
					>
						<button
							className={BUTTON_RESET_CLASSES}
							onClick={viewPriceDetail}
						>
							{getFormattedValue(formatted)}
						</button>
					</dd>
					<CaDisclaimer dealerAddress={dealerAddress} />
				</dl>
			);
		}
		return pricingSummary;
	}
}

PricingSummaryUpdated.propTypes = {
	prefs: PropTypes.shape({
		showStickySidebarLoanLease: PropTypes.string
	}).isRequired,
	summary: PropTypes.arrayOf(
		PropTypes.shape({
			_type: PropTypes.string.isRequired,
			formatted: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			value: PropTypes.number.isRequired
		})
	).isRequired,
	viewPriceDetail: PropTypes.func.isRequired,
	widgetInfo: PropTypes.shape({
		windowId: PropTypes.string.isRequired,
		widgetName: PropTypes.string.isRequired,
		deviceType: PropTypes.string.isRequired
	}).isRequired,
	dealerAddress: PropTypes.shape({
		address1: PropTypes.string,
		address2: PropTypes.string,
		address3: PropTypes.string,
		city: PropTypes.string,
		state: PropTypes.string,
		postalCode: PropTypes.string,
		country: PropTypes.string
	}),
	isMobile: PropTypes.bool
};

PricingSummaryUpdated.defaultProps = {
	dealerAddress: {},
	isMobile: false
};



// WEBPACK FOOTER //
// ./src/components/PricingSummaryUpdated.jsx